// @flow

import { createSelector } from "reselect"
import { Record, Map } from "immutable"

import type { FlightId, PassengerId } from "mmb/types/primitiveTypes"
import { bookingStateSelector } from "../selector"
// eslint-disable-next-line kiwicom/prefer-nitro-translate-function
import translateSelector from "common/services/intl/translateSelector"
import {
  LOAD_SEATS_FAILURE,
  LOAD_SEATS_REQUEST,
  LOAD_SEATS_SUCCESS,
} from "mmb/scenes/AccountBooking/components/Booking/services/consts"

const CHANGE_FLIGHT = "bookingSeating/CHANGE_FLIGHT"
const SELECT_SEAT = "bookingSeating/SELECT_SEAT"
const BOOK_SEAT = "bookingSeating/BOOK_SEAT"
const RELEASE_SEAT = "bookingSeating/RELEASE_SEAT"
const CLEAR_BOOKED_SEATS = "bookingSeating/CLEAR_BOOKED_SEATS"

type BookingSeatingStateType = Map<string, any> // eslint-disable-line flowtype/no-weak-types

export const InitialState = Record({
  activeFlight: null,
  bookedSeats: Map(),
  selectedSeat: null,
  loading: false,
})

// eslint-disable-next-line flowtype/no-weak-types
export default (state: BookingSeatingStateType = new InitialState(), action: Object) => {
  switch (action.type) {
    case CHANGE_FLIGHT: {
      return state.set("activeFlight", action.payload.flightId)
    }

    case SELECT_SEAT: {
      return state.set("selectedSeat", action.payload.seatId)
    }

    case BOOK_SEAT: {
      let tempState = state
      const { flightId, seatId, passengerId } = action.payload
      const flight = tempState.getIn(["bookedSeats", flightId])

      if (flight) {
        const passengerAtSeat = flight.findKey(seat => seat === seatId)
        tempState = tempState.deleteIn(["bookedSeats", flightId, passengerAtSeat])
      }

      return tempState.setIn(["bookedSeats", flightId, passengerId], seatId)
    }

    case RELEASE_SEAT: {
      const { flightId, passengerId } = action.payload

      return state.deleteIn(["bookedSeats", flightId, passengerId])
    }

    case CLEAR_BOOKED_SEATS: {
      return state.set("bookedSeats", Map()).set("activeFlight", null)
    }

    case LOAD_SEATS_REQUEST:
      return state.set("loading", true)

    case LOAD_SEATS_SUCCESS:
    case LOAD_SEATS_FAILURE:
      return state.set("loading", false)

    default: {
      return state
    }
  }
}

// ACTIONS
// ===
export const changeFlight = (flightId: FlightId) => ({
  type: CHANGE_FLIGHT,
  payload: {
    flightId,
  },
})

export const bookSeat = (flightId: FlightId, seatId: string, passengerId: PassengerId) => ({
  type: BOOK_SEAT,
  payload: {
    flightId,
    seatId,
    passengerId,
  },
})

export const releaseSeat = (flightId: FlightId, passengerId: PassengerId) => ({
  type: RELEASE_SEAT,
  payload: {
    flightId,
    passengerId,
  },
})

export const selectSeat = (seatId: ?string) => ({
  type: SELECT_SEAT,
  payload: {
    seatId,
  },
})

export const clearBookedSeats = () => ({
  type: CLEAR_BOOKED_SEATS,
})

// SELECTORS
// ===

export const loadSeatmapErrorMessageSelector: $FlowFixMe = createSelector(
  [bookingStateSelector, translateSelector],
  (bookingState, translate): ?string => {
    if (
      bookingState.error &&
      bookingState.error.type === LOAD_SEATS_FAILURE &&
      typeof bookingState.error.error === "string"
    ) {
      return bookingState.error.error
    }

    if (bookingState.error) {
      return translate("common.api_error")
    }

    return null
  },
)
